import * as Sentry from '@sentry/react';
import React from 'react';
import {
  Navigate,
  Route,
  Routes as RouterRoutes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import {WithAuth, WithLoggedInAuth, WithLoggedOutAuth} from './components/Auth';
import {ShouldRefreshContext} from './components/Utility/VersionUpdateMonitor';
import {WithOrgIdPrefix} from './hooks/useCurrentOrgIdPrefix';
import {shouldShowOrgSwitcherUI} from './lib/api/auth';
import {EvalProduct} from './lib/api/eval';
import featureFlags from './lib/featureFlags';
import {LOGIN_APP_URL, makeUuidPrefix, redirectToLoginApp} from './lib/routeUtils';
import SignOut from './pages/Auth/SignOut';
import EvalSiteDetailRouter from './pages/Eval/EvalSiteDetailRouter';
import EvalForecastDetail from './pages/Eval/ForecastDetail';
import EvalGlobalOverview from './pages/Eval/GlobalOverview';
import EvalOverview from './pages/Eval/Overview';
import OrganizationSwitch from './pages/OrganizationSwitch';
import ProjectPage from './pages/Project';
import RegionalReportPage from './pages/Project/RegionalReport';
import ProjectListPage from './pages/ProjectList';
import Settings from './pages/Settings';
import ApiSettings from './pages/Settings/API';
import ManageUsers from './pages/Settings/ManageUsers';
import Invite from './pages/Settings/ManageUsers/Invite';
import Notifications from './pages/Settings/Notifications';
import Observations from './pages/Settings/Observations';
import Organization from './pages/Settings/Organization';
import SetupWizardPage from './pages/Setup/SetupWizardPage';
import SetupWizardSelectPage from './pages/Setup/SetupWizardSelectPage';

// Used to inform Sentry about :params in the URLs. See:
// https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router
//
// The wrap has to happen after Sentry.init(): the tracing integration hands
// Sentry the router hooks during setup, and withSentryReactRouterV7Routing
// quietly returns Routes unwrapped when they aren't there yet. Module bodies run
// before index.tsx calls initSentry(), so build it on first render instead.
let wrappedRoutes: React.ComponentType<React.ComponentProps<typeof RouterRoutes>> | undefined;

function SentryRoutes(props: React.ComponentProps<typeof RouterRoutes>) {
  const Wrapped = (wrappedRoutes ??= Sentry.withSentryReactRouterV7Routing(RouterRoutes));
  return <Wrapped {...props} />;
}

// The Eval overview is only reachable at these two products. Any other value
// falls through to the catch-all, matching the `:product(short-term|seasonal)`
// pattern this replaced — react-router 7 has no regex path params.
const EVAL_PRODUCTS: EvalProduct[] = ['short-term', 'seasonal'];

function SignInRedirect() {
  // We want to handle this in the login app from now on.
  return (
    <WithLoggedOutAuth>
      {(_auth) => {
        redirectToLoginApp(LOGIN_APP_URL);
        return <div />; // unused, need to return something to satisfy the type checker
      }}
    </WithLoggedOutAuth>
  );
}

function SignUpRedirect() {
  const {type, key} = useParams();
  // We want to handle this in the login app from now on.
  window.location.assign(`${LOGIN_APP_URL}/signup/${type}/${key}`);
  return null;
}

function ForgotPasswordRedirect() {
  // We want to handle this in the login app from now on. <Navigate> can't leave
  // this origin — it resolves an absolute URL as a relative path.
  window.location.assign(`${LOGIN_APP_URL}/forgotPassword`);
  return null;
}

function ProjectsRedirect() {
  const location = useLocation();
  return (
    <WithLoggedInAuth>
      {(_, _currentUser, {currentOrganization, profileOrganizations}) => {
        if (shouldShowOrgSwitcherUI(profileOrganizations)) {
          return <Navigate to="/organizations" replace />;
        }
        return (
          <Navigate
            to={`/${makeUuidPrefix(currentOrganization.id)}${location.pathname}${
              location.hash || ''
            }${location.search || ''}`}
            replace
          />
        );
      }}
    </WithLoggedInAuth>
  );
}

export default function Routes() {
  // Determines if the app needs a refresh, then will do so on refresh on next route transition.
  const shouldRefresh = React.useContext(ShouldRefreshContext);
  const navigate = useNavigate();
  // Using a ref here to decouple state updates from component updates.
  const refreshPending = React.useRef(false);

  React.useEffect(() => {
    refreshPending.current = shouldRefresh;
  }, [shouldRefresh]);

  // If we have a refresh pending and this component has updated, usually on route change, refresh the app.
  if (refreshPending.current) {
    navigate(0);
  }
  return (
    <SentryRoutes>
      <Route path="/signin/*" element={<SignInRedirect />} />

      <Route path="/signout/*" element={<WithAuth>{(auth) => <SignOut auth={auth} />}</WithAuth>} />

      <Route
        path="/eval"
        element={
          <WithLoggedInAuth>
            {(_auth, currentUser) =>
              featureFlags.EVAL_DASHBOARD(currentUser) ? (
                <Navigate to="/eval/short-term" replace />
              ) : (
                <Navigate to="/projects" replace />
              )
            }
          </WithLoggedInAuth>
        }
      />

      {EVAL_PRODUCTS.map((product) => (
        <Route
          key={product}
          path={`/eval/${product}`}
          element={
            <WithLoggedInAuth>
              {(_auth, currentUser) =>
                featureFlags.EVAL_DASHBOARD(currentUser) ? (
                  <EvalGlobalOverview product={product} />
                ) : (
                  <Navigate to="/projects" replace />
                )
              }
            </WithLoggedInAuth>
          }
        />
      ))}

      <Route path="/signup/:type/:key?/*" element={<SignUpRedirect />} />

      <Route path="/forgotPassword/*" element={<ForgotPasswordRedirect />} />

      <Route path="/projects" element={<ProjectsRedirect />} />

      <Route
        path="/:currentOrgIdPrefix/projects/:projectId/regional-report"
        element={
          <WithOrgIdPrefix>
            <WithLoggedInAuth>
              {(auth, currentUser) =>
                featureFlags.REGIONAL_REPORT(currentUser) ? (
                  <RegionalReportPage auth={auth} currentUser={currentUser} />
                ) : (
                  <Navigate to="/projects" replace />
                )
              }
            </WithLoggedInAuth>
          </WithOrgIdPrefix>
        }
      />

      <Route
        path="/:currentOrgIdPrefix/projects/:projectId/*"
        element={
          <WithOrgIdPrefix>
            <WithLoggedInAuth>
              {(auth, currentUser) => <ProjectPage auth={auth} currentUser={currentUser} />}
            </WithLoggedInAuth>
          </WithOrgIdPrefix>
        }
      />

      <Route
        path="/:currentOrgIdPrefix/eval/:siteId/:forecastId/*"
        element={
          <WithOrgIdPrefix>
            <WithLoggedInAuth>
              {(_auth, currentUser) =>
                featureFlags.EVAL_DASHBOARD(currentUser) ? (
                  <EvalForecastDetail />
                ) : (
                  <Navigate to="/projects" replace />
                )
              }
            </WithLoggedInAuth>
          </WithOrgIdPrefix>
        }
      />

      <Route
        path="/:currentOrgIdPrefix/eval/:siteId"
        element={
          <WithOrgIdPrefix>
            <WithLoggedInAuth>
              {(_auth, currentUser) =>
                featureFlags.EVAL_DASHBOARD(currentUser) ? (
                  <EvalSiteDetailRouter />
                ) : (
                  <Navigate to="/projects" replace />
                )
              }
            </WithLoggedInAuth>
          </WithOrgIdPrefix>
        }
      />

      <Route
        path="/:currentOrgIdPrefix/eval"
        element={
          <WithOrgIdPrefix>
            <WithLoggedInAuth>
              {(_auth, currentUser) =>
                featureFlags.EVAL_DASHBOARD(currentUser) ? (
                  <EvalOverview />
                ) : (
                  <Navigate to="/projects" replace />
                )
              }
            </WithLoggedInAuth>
          </WithOrgIdPrefix>
        }
      />

      <Route
        path="/:currentOrgIdPrefix/projects"
        element={
          <WithOrgIdPrefix>
            <WithLoggedInAuth>
              {(_, _currentUser, {currentOrganization}) => {
                return <ProjectListPage currentOrganization={currentOrganization} />;
              }}
            </WithLoggedInAuth>
          </WithOrgIdPrefix>
        }
      />

      <Route
        path="/:currentOrgIdPrefix/settings/*"
        element={
          <WithOrgIdPrefix>
            <WithLoggedInAuth>
              {(auth, currentUser) => {
                return (
                  <RouterRoutes>
                    <Route path="users/*" element={<ManageUsers currentUser={currentUser} />} />
                    <Route path="invite/*" element={<Invite currentUser={currentUser} />} />
                    <Route path="api/*" element={<ApiSettings />} />

                    <Route
                      path="notifications/*"
                      element={<Notifications currentUser={currentUser} />}
                    />
                    <Route
                      path="organization/*"
                      element={<Organization currentUser={currentUser} />}
                    />
                    {featureFlags.OBSERVATIONS_UPLOAD(currentUser) && (
                      <Route path="observations/*" element={<Observations />} />
                    )}
                    {/* Fallback, matching the v5 pathless <Route> this replaced: renders
                        for bare /settings and for any sub-path not claimed above. */}
                    <Route path="*" element={<Settings currentUser={currentUser} auth={auth} />} />
                  </RouterRoutes>
                );
              }}
            </WithLoggedInAuth>
          </WithOrgIdPrefix>
        }
      />

      <Route
        path="/:currentOrgIdPrefix/setup"
        element={
          <WithOrgIdPrefix>
            <WithLoggedInAuth>
              {(_auth, currentUser) =>
                featureFlags.SETUP_WIZARD(currentUser) ? (
                  <SetupWizardSelectPage />
                ) : (
                  <Navigate to="/projects" replace />
                )
              }
            </WithLoggedInAuth>
          </WithOrgIdPrefix>
        }
      />

      <Route
        path="/:currentOrgIdPrefix/setup/:orgId/:projectId/*"
        element={
          <WithOrgIdPrefix>
            <WithLoggedInAuth>
              {(_auth, currentUser) =>
                featureFlags.SETUP_WIZARD(currentUser) ? (
                  <SetupWizardPage />
                ) : (
                  <Navigate to="/projects" replace />
                )
              }
            </WithLoggedInAuth>
          </WithOrgIdPrefix>
        }
      />

      <Route
        path="/organizations/*"
        element={
          <WithLoggedInAuth>
            {(auth, profile, {currentOrganization, profileOrganizations}) =>
              shouldShowOrgSwitcherUI(profileOrganizations) ? (
                <OrganizationSwitch
                  auth={auth}
                  currentOrganization={currentOrganization}
                  profileOrganizations={profileOrganizations}
                  profile={profile}
                />
              ) : (
                <Navigate to="/projects" replace />
              )
            }
          </WithLoggedInAuth>
        }
      />

      <Route path="*" element={<Navigate to="/signin" replace />} />
    </SentryRoutes>
  );
}
